import React, { useState } from 'react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { FileCheckIcon } from 'lucide-react';
import { assignments as seedAssignments } from '../../data/academics';
import type { Assignment } from '../../types/academic';
const FILTERS = ['Open', 'Submitted', 'Graded', 'All'] as const;
type Filter = (typeof FILTERS)[number];
const STATUS_LABEL: Record<Assignment['status'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  graded: 'Graded',
  overdue: 'Overdue'
};
function tone(status: Assignment['status']) {
  if (status === 'overdue') return 'danger' as const;
  if (status === 'graded') return 'ok' as const;
  if (status === 'submitted') return 'brand' as const;
  if (status === 'in_progress') return 'warn' as const;
  return 'neutral' as const;
}
export function AssignmentBoard() {
  const [assignments, setAssignments] = useState<Assignment[]>(seedAssignments);
  const [filter, setFilter] = useState<Filter>('Open');
  const visible = assignments.filter(item => {
    if (filter === 'All') return true;
    if (filter === 'Submitted') return item.status === 'submitted';
    if (filter === 'Graded') return item.status === 'graded';
    return item.status === 'not_started' || item.status === 'in_progress' || item.status === 'overdue';
  });
  function markSubmitted(id: string) {
    setAssignments(current => current.map(item => item.id === id ? {
      ...item,
      status: 'submitted'
    } : item));
  }
  return <Panel title="Assignments" description="Track your own submissions. Marks appear once you record them or a lecturer publishes them." action={<ChoiceGroup options={FILTERS} value={filter} onChange={setFilter} size="sm" data-id="element-235" data-mp="1l73xmhvozu0a" />} bodyClassName="p-0" data-id="element-234" data-mp="te3g2x3wsmbo">
      {visible.length === 0 ? <EmptyState icon={<FileCheckIcon className="h-4 w-4" data-id="element-237" data-mp="1zukp7kkc1ms7" />} title="Nothing here" description={`No assignments match the “${filter}” filter right now.`} data-id="element-236" data-mp="1pzx0f07los9q" /> : <ul className="divide-y divide-line" data-id="element-238" data-mp="1qte5z3qatxv9">
          {visible.map(item => <li key={item.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center" data-id="element-239" data-mp="1qvh46b1luxb2">
              <div className="min-w-0 flex-1" data-id="element-240" data-mp="1lwnqahiep4ad">
                <div className="flex flex-wrap items-center gap-2" data-id="element-241" data-mp="1gvglmrbk8l7x">
                  <p className="text-sm font-medium text-ink" data-id="element-242" data-mp="1c4hx54gf4gtl">{item.title}</p>
                  <Badge tone={tone(item.status)} data-id="element-243" data-mp="1c2doyabhkvni">{STATUS_LABEL[item.status]}</Badge>
                </div>
                <p className="cz-num mt-1 text-[13px] text-ink-muted" data-id="element-244" data-mp="180ikwy268461">
                  {item.courseCode} · {item.weight}% of course mark · {item.dueLabel}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3" data-id="element-245" data-mp="y6jnudgyfaky">
                {item.score !== undefined && <span className="cz-num text-sm font-semibold text-ink" data-id="element-246" data-mp="j1p7qo847nto">{item.score}%</span>}
                {(item.status === 'not_started' || item.status === 'in_progress' || item.status === 'overdue') && <Button size="sm" variant="secondary" onClick={() => markSubmitted(item.id)} data-id="element-247" data-mp="t0mubllzd7a1">
                    Mark submitted
                  </Button>}
              </div>
            </li>)}
        </ul>}
    </Panel>;
}